import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import type { RootState } from './index';
import { centralLogout } from './slices/centralAuthSlice';
import { setBootUnavailable } from './slices/bootContextSlice';
import {
  classifyCentralFailure,
  type CentralFailureAction,
} from '../auth/centralFailureContract';
import { denialReasonFor, raiseBootDenial } from '../auth/bootDenial';
import {
  renewAccessTokenSilently,
  __resetSilentAuthorizeState,
} from '../auth/silentAuthorize';

const API_URL = import.meta.env.VITE_API_URL || '/api/v1';

/**
 * Base fetch query with Central access token.
 */
const rawBaseQuery = fetchBaseQuery({
  baseUrl: API_URL,
  prepareHeaders: (headers, { getState }) => {
    const state = getState() as RootState;
    const token = state.centralAuth?.accessToken;
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    return headers;
  },
});

// ──────────────────────────────────────────────────────────────
// Renewal state machine
// ──────────────────────────────────────────────────────────────
//
// WHAT USED TO BE HERE, AND WHY IT IS GONE
//   A second `fetchBaseQuery` posted to a Central-session refresh path rewritten
//   off BeHeard's OWN api base — so a SAME-ORIGIN path on BeHeard's backend,
//   which mounts no auth router at all. That request could only ever 404: one
//   wasted round trip on the first 401 of every tab's life, then a sticky
//   endpoint-absent bit, then a full-page Central login. The path, the second
//   base query and the sticky bit are all deleted, and
//   `store/__tests__/refreshPathPremise.test.ts` now pins their absence.
//
// WHAT RENEWS THE TOKEN NOW
//   `auth/silentAuthorize.ts`: a `prompt=none` authorize in a hidden frame,
//   against the live Central SSO session cookie, followed by a PKCE code
//   exchange — browser-direct to Central, no backend route on either side. The
//   SPA stays mounted, `centralLogout()` is NOT called on the recoverable path,
//   and the user keeps their page and their unsaved work.
//
//   Its single-flight lives in that module rather than here, because TWO
//   perimeters renew: this base query and `auth/reportCentralFailure.ts` (the
//   raw-`fetch` Central reads, one of which polls). A collapse that lived here
//   would let a polling notification read start a second authorize.
//
// THE THREE DISPOSITIONS, which must never be confused
//   ok                   token replaced -> retry the original request ONCE
//   SESSION_OVER         no live Central session -> Central login, auth cleared
//   CENTRAL_UNAVAILABLE  the attempt could not COMPLETE (blocked frame, blocked
//                        storage, timeout, a failed exchange) -> keep the
//                        credential, keep the session, NEVER login
//
// State kept here:
//   loggedOutForToken : the access token we have already ended the session for.
//                       One login transition per credential generation, so 20
//                       in-flight dead requests produce ONE transition — and a
//                       later in-SPA re-login re-arms the latch instead of
//                       going silent for ever.
// ──────────────────────────────────────────────────────────────

let loggedOutForToken: string | null = null;

/** Test seam: reset the module-scope state machine between cases. */
export function __resetAuthStateForTests(): void {
  loggedOutForToken = null;
  __resetSilentAuthorizeState();
}

/**
 * Send the user to Central login — at most ONCE per credential generation.
 *
 * AuthGuard renders a single `<Navigate to="/login" replace />` off
 * `isAuthenticated`, so clearing auth IS the login transition.
 */
function signalCentralLogin(
  dispatch: (action: unknown) => unknown,
  token: string | null,
  reason: string,
): void {
  // Re-arm ONLY when a genuinely new credential has been issued since the last
  // end-of-session. `centralLogout()` nulls the stored token, so a null token
  // must never count as a new generation — otherwise the 2nd of 20 in-flight
  // verdicts would read `token === null`, conclude "new credential", and log out
  // a second time.
  if (loggedOutForToken !== null && (token === null || token === loggedOutForToken)) return;
  loggedOutForToken = token ?? loggedOutForToken;
  console.warn('[api] session over:', reason);
  dispatch(centralLogout());
}

/**
 * Route a membership / app-assignment refusal to suite navigation. Never login.
 * The overlay is raised WITH its reason (BH-06, auth/bootDenial.ts): without
 * it the overlay titled an access denial "Workspace Closed".
 */
function signalSuiteNavigation(
  dispatch: (action: unknown) => unknown,
  decision: { action: CentralFailureAction; code: string | null; reason: string },
  body: unknown,
): void {
  console.warn('[api] access denied for this workspace/app:', decision.reason);
  raiseBootDenial(dispatch, denialReasonFor(decision.action) ?? 'app_access_denied', decision.code, body);
}

/**
 * App admission could not consult Central for a READ (503 CENTRAL_UNAVAILABLE,
 * backend requireAppAdmission): nothing in the app can be read until Central
 * answers, so show the Central-outage overlay with its Retry (contract §1:
 * 5xx/timeout -> unavailable+retry). Never a login, credential untouched.
 *
 * Narrow on purpose:
 *   - only that code — an ordinary 5xx on one data endpoint stays that page's
 *     own error (a single failing endpoint must not blank the whole app);
 *   - only reads — a refused WRITE keeps its inline, retryable copy
 *     (utils/attemptErrors), so the overlay never unmounts a form or a
 *     recording mid-attempt;
 *   - only from a READY boot — it never overwrites a terminal verdict, a
 *     closed workspace or an access denial (precedence 1-3 beat 4).
 */
function signalAdmissionOutageOnRead(
  args: string | FetchArgs,
  api: { dispatch: (action: unknown) => unknown; getState: () => unknown },
  decision: { code: string | null; reason: string },
): void {
  if (decision.code !== 'CENTRAL_UNAVAILABLE') return;
  const method = (typeof args === 'string' ? 'GET' : args.method ?? 'GET').toUpperCase();
  if (method !== 'GET' && method !== 'HEAD') return;
  const state = api.getState() as RootState;
  if (!state.centralAuth?.isAuthenticated) return;
  if (state.beheardAuthExt?.isSwitchingWorkspace) return;
  if (state.bootContext?.bootStatus !== 'ready') return;
  console.warn('[api] Central unavailable for a read — outage overlay, credential kept:', decision.reason);
  api.dispatch(setBootUnavailable('App admission could not reach Central'));
}

const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions,
) => {
  const result = await rawBaseQuery(args, api, extraOptions);
  if (!result.error) return result;

  // ── The ONE classifier, shared with the boot perimeter ──
  //
  // Classification is on the machine-readable CODE whenever Central supplies
  // one, never on the status alone: a 401 means "refresh me" for one code and
  // "you are finished" for another, and a 403 means "no Central session"
  // (login) for one code and "membership is dead" (never login) for another.
  const decision = classifyCentralFailure({
    status: result.error.status,
    body: result.error.data,
  });

  switch (decision.action) {
    // A closed workspace is not an auth failure. Never a login: signing in
    // again cannot reopen a workspace. Central spells it at 400 AND 409.
    case 'WORKSPACE_CLOSED':
      // With its reason, so a stale denial reason can never title it (BH-06).
      raiseBootDenial(api.dispatch, 'workspace_closed', decision.code, result.error.data);
      return result;

    // Membership gone, or this app not assigned to the member. A login would
    // mint a valid token, authorise the same dead membership, land back on the
    // same 403 and loop for ever.
    case 'WORKSPACE_PICKER':
    case 'APP_ACCESS_DENIED':
      signalSuiteNavigation(api.dispatch, decision, result.error.data);
      return result;

    // 5xx / network / timeout on ordinary API traffic. Never a login, never a
    // refresh, and deliberately NOT the full-screen Central-outage overlay:
    // that overlay is about CENTRAL identity being unreachable, and it is
    // raised by the Central perimeter (BootGate / switch-context). A single
    // failing data endpoint must not blank the whole app. The one exception is
    // app admission refusing a READ because Central itself could not be asked
    // (503 CENTRAL_UNAVAILABLE) — see signalAdmissionOutageOnRead.
    case 'OUTAGE':
      signalAdmissionOutageOnRead(args, api, decision);
      return result;
    // An auth-shaped answer we cannot interpret — a code-less 401, a
    // lower_snake service code, an unrecognised code. A SERVICE failure, never
    // a verdict on the user's session, so never a login.
    case 'SERVICE_FAILURE':
    case 'PASS_THROUGH':
      return result;

    case 'CENTRAL_LOGIN':
    case 'RETRY_AFTER_REFRESH':
      break;
  }

  // ── From here on the failure concerns this user's credential ──

  const state = api.getState() as RootState;
  const token = state.centralAuth?.accessToken ?? null;

  // Only Central-authenticated users have a session to end or renew.
  if (!state.centralAuth?.isAuthenticated) return result;

  // Never touch auth during a workspace switch — the switch flow manages its
  // own tokens and a stale 401 from a background re-fetch must not race it.
  if (state.beheardAuthExt?.isSwitchingWorkspace) return result;

  // The session itself is over. Straight to Central login: no refresh is spent,
  // because no refresh can revive a dead session.
  if (decision.action === 'CENTRAL_LOGIN') {
    signalCentralLogin(api.dispatch, token, decision.reason);
    return result;
  }

  // ── RETRY_AFTER_REFRESH: the access token aged out (15 min). ──
  //
  // THE CLAUSE THIS IS HERE TO HONOUR: "access token expires after 15 minutes
  // -> silent refresh -> the user stays in the app". So: renew in the
  // background against the live Central session and retry. No navigation, no
  // unmount, no `centralLogout()` — the SPA does not learn that anything
  // happened beyond a new token appearing in the store.
  //
  // `renewAccessTokenSilently` is single-flight across the whole app (a SHARED
  // PROMISE, not a mutex — see its module header), so twenty simultaneous 401s
  // produce exactly ONE authorize and exactly twenty retries, and the token is
  // written exactly once.
  const renewal = await renewAccessTokenSilently(api.dispatch);

  if (renewal.ok) {
    // A working renewal re-arms the login latch for the new credential.
    loggedOutForToken = null;
    // Retry the original request exactly ONCE. No retry storm.
    return rawBaseQuery(args, api, extraOptions);
  }

  if (renewal.disposition === 'CENTRAL_UNAVAILABLE') {
    // The attempt could not COMPLETE — a blocked frame, partitioned storage, a
    // timeout, a failed code exchange. None of that is evidence about the
    // user's session, so the credential is kept and the request's own failure
    // is surfaced for the caller to retry. Destroying the session here would
    // mean a browser that blocks iframes logs people out, which is a worse bug
    // than the full-page round trip this path replaced.
    console.warn('[api] silent renewal unavailable — credential kept, no login:', renewal.reason);
    return result;
  }

  // SESSION_OVER: Central has no live SSO session to authorize against. This is
  // the one genuinely terminal outcome, and the mandatory fallback.
  signalCentralLogin(api.dispatch, token, `silent renewal refused: ${renewal.reason}`);
  return result;
};

/**
 * Main BeHeard API.
 *
 * Uses the Central access token. Every failure is classified by
 * `src/auth/centralFailureContract.ts` — the same classifier the boot perimeter
 * uses — so the two can never disagree about what a code means:
 *
 *   TOKEN_EXPIRED / ACCESS_TOKEN_EXPIRED -> ONE silent authorize (single-flight,
 *                                           shared promise), ONE retry, SPA
 *                                           stays mounted, no login screen
 *   SESSION_EXPIRED / SESSION_REVOKED /
 *     AUTH_REQUIRED / INVALID_TOKEN      -> Central login, NO silent attempt
 *                                           wasted first
 *   WORKSPACE_CLOSED (400 or 409)        -> workspace-closed UI, NEVER login
 *   MEMBERSHIP_* / APP_ACCESS_DENIED     -> suite navigation, NEVER login
 *   5xx / network / timeout              -> surfaced, credential kept, NEVER login
 *   code-less or unrecognised 401        -> service failure, NEVER login
 *   a silent attempt that cannot
 *     complete (blocked / timed out)     -> CENTRAL_UNAVAILABLE: surfaced and
 *                                           retryable, credential and session
 *                                           untouched, NEVER login
 *
 * No retry storm. No silent swallow. No dead same-origin refresh route.
 */
export const api = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['User', 'Scenarios', 'Sessions', 'VoiceAvatars', 'Progress', 'Portfolio', 'Highlights', 'Marketplace', 'HireRequests', 'ListenStats', 'Billing', 'SkillCatalog', 'SkillDomains', 'SkillCategories', 'SkillObjectives', 'SkillProgress', 'Templates', 'Assessments', 'PublicProfile', 'ViewStats', 'Petals', 'Events', 'AdminProfiles', 'AdminReports', 'Usage', 'AdminUsage', 'Blueprints', 'NpcPersonas', 'NpcLevels', 'NpcProgress', 'SavedCandidates', 'Evaluations', 'ContactRequests', 'RecruiterDashboard', 'Organizations', 'OrgMembers', 'TrainingModules', 'TrainingAssignments', 'ContentSets', 'CoachProfile', 'Engagements', 'Bookings', 'SharedSessions', 'OrgAnalytics', 'CoachAnalytics', 'Timeline', 'Referrals', 'LearningModules', 'MarketplacePublish', 'Capabilities', 'Endorsements', 'ShareLinks', 'Discovery', 'ScenarioProgression', 'Rubrics'],
  endpoints: () => ({}),
});
